import React from "react";
import { useNavigate } from "react-router-dom";
import { Formik, Form, FormikHelpers } from "formik";
import { Button, HelperPrompt, Textarea, SequenceStepper, DictationMessage } from "components/core";
import { DataContainer } from "containers/DataContainer";
import { ReactComponent as IconDonutSpinner } from "../core/assets/img/donut-spinner.svg";
import { ReactComponent as IconInfo } from "../core/assets/img/info.svg";
import { ICatchUp } from "types";
import { catchUpValidationSchema } from "utils/validations";
import { useCatchUpProcessing } from "hooks";
import "./onboardingCatchup.css";

interface Values {
    catchUp: ICatchUp;
}

export interface OnboardingCatchupProps {
    embedded?: boolean;
}

export const OnboardingCatchup: React.FC<OnboardingCatchupProps> = (props) => {
    const { embedded = false } = props;
    const {
        EmptyCatchup,
        catchups,
        setCatchups,
        user,
    } = DataContainer.useContainer();

    const navigate = useNavigate();
    const {
        submittingForm,
        setSubmittingForm,
        getGeneratedWorkExperiences,
        getGeneratedSuggestions,
        getGeneratedSummary,
        getGeneratedSentiment
    } = useCatchUpProcessing();

    const catchUp: ICatchUp = new EmptyCatchup(user);

    const initialValues = {
        catchUp,
    };

    const IconDonutSpinnerSVG = <IconDonutSpinner />;

    return (
        <section className="pattern--container--flow pattern--container--flow-narrow">
            <div className="pattern--container--flow__heading pattern--container--flow__heading--accessory">
                <h1 className="type--heading-1">Catch Up</h1>
                <div className="accessory--resizeable">
                    <SequenceStepper length={6} currentIndex={5} />
                </div>
            </div>
            <div className="main-content-card main-content-card--no-texture pattern--form-sheet pattern--container--flow__body">
                <p className="type--body--large">
                    Catch Ups are a way to use a journal entry to quickly keep your Experience up to date on a
                    regular basis.
                </p>
                <p className="type--body--large">
                    What have you worked on recently? How did it go? Were there results? What could improve
                    next time? Do you feel closer to your goals or further away?
                </p>
                <Formik
                    initialValues={initialValues}
                    onSubmit={async (values: Values, { setSubmitting }: FormikHelpers<Values>) => {
                        setSubmittingForm(true);
                        const newCatchUp: ICatchUp = values.catchUp;
                        setSubmitting(true);
                        newCatchUp.name = new Date().toLocaleDateString("en-us", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                        });

                        getGeneratedSummary(values.catchUp.description)
                            .then((value) => {
                                const summary: string = value;
                                if (!summary.includes("Error")) {
                                    newCatchUp.summary = summary;
                                } else {
                                    newCatchUp.summary = "";
                                }
                            })
                            .then(() => getGeneratedWorkExperiences(values.catchUp.description))
                            .then((value) => {
                                const experiences: string = value;
                                if (!experiences.includes("Error")) {
                                    values.catchUp.experiences = experiences;
                                } else {
                                    newCatchUp.experiences = "";
                                }
                            })
                            .then(() => getGeneratedSuggestions(values.catchUp.description))
                            .then((value) => {
                                const suggestions: string = value;
                                if (!suggestions.includes("Error")) {
                                    values.catchUp.suggestions = suggestions;
                                } else {
                                    newCatchUp.suggestions = "";
                                }
                            })
                            .then(() => getGeneratedSentiment(values.catchUp.description))
                            .then((value) => {
                                const sentiment: string = value;
                                if (!sentiment.includes("Error")) {
                                    newCatchUp.sentiment = sentiment;
                                } else {
                                    newCatchUp.sentiment = "";
                                }
                            })
                            .then(() => {
                                setCatchups([...[newCatchUp], ...catchups]);
                                setSubmitting(false);
                                setSubmittingForm(false);
                                navigate("/onboarding/catchup/" + values.catchUp.itemId, {
                                    replace: true,
                                });
                            });
                    }}
                    validations={catchUpValidationSchema}
                >
                    {({ values, submitForm }) => (
                        <>
                            <Form className="catchup-creation-fields">
                                <>
                                    <div className="catchup-creation-fields__description">
                                        <Textarea
                                            label="Catch Up Description"
                                            name={`catchUp.description`}
                                            placeholder="What are the details?"
                                            rows={5}
                                            maxLength={8096}
                                        />
                                    </div>
                                </>
                            </Form>
                            <div className="info-pane-wrapper">
                                <div className="info-pane-wrapper__header">
                                    <h2 className="type--heading-6">Pro Tip</h2>
                                    <IconInfo />
                                </div>
                                <div className="info-pane-wrapper__body">
                                    <p className="type--body--large">
                                        If typing out what you recall about your work seems tiring or
                                        intimidating. Use your device’s dictation abilities to speak out loud,
                                        like you were telling a friend.
                                    </p>
                                    <HelperPrompt label="Show Me How">
                                        <DictationMessage />
                                    </HelperPrompt>
                                </div>
                            </div>
                            {!embedded && (
                                <div className="pattern--button-row">
                                    <Button
                                        label="Back"
                                        buttonTitle="Back to Resume Review"
                                        buttonType="utility"
                                        onClick={() => navigate("/onboarding/resume/review")}
                                    />
                                    <Button
                                        label="Next"
                                        buttonTitle="Finish Catch Up"
                                        buttonType="secondary"
                                        disabled={values.catchUp.description !== "" ? false : true}
                                        type="button"
                                        leadingIcon={submittingForm && IconDonutSpinnerSVG}
                                        onClick={() => submitForm()}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </Formik>
            </div>
        </section>
    );
};
